import React, { useCallback, useRef } from 'react';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import { css, useTheme } from '@emotion/react';

import { useBusinessSolutionsData, useSolutionsSelectProps } from './hooks';
import { BUSINESS_SOLUTIONS_VARIABLES } from './constants';

import MobileSelect, { MOBILE_SELECT_VARIABLES } from 'components/MobileSelect';
import { filterProps } from 'utils/helpers';

export interface ISolutionsTabs {
  solutions: ReturnType<typeof useBusinessSolutionsData>['solutions'];
  activeIndex: number;
  handleChange: (index: number) => void;
}

const Container = styled.div`
  ${({ theme: { mq } }) => css`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 1.5rem;
    margin-bottom: 3rem;

    ${mq.md} {
      display: none;
    }
  `}
`;

const Tab = styled('div', filterProps('isActive'))<{
  isActive: boolean;
}>`
  ${({ theme: { durations }, isActive }) => css`
    display: grid;
    grid-template-areas: 'icon' 'label';
    grid-template-rows: 4rem 1fr;
    row-gap: 1.25rem;
    justify-items: center;
    transition: background-color ${durations.long} ease-in-out;
    border-radius: 2rem;
    background-color: ${isActive
      ? `var(${BUSINESS_SOLUTIONS_VARIABLES.foregroundColor}) `
      : 'transparent'};
    cursor: pointer;
    padding: 2.5rem 0;
    width: 100%;
  `}
`;

const Icon = styled('div', filterProps('url', 'isActive'))<{
  url: string;
  isActive: boolean;
}>`
  ${({ url, isActive }) => css`
    grid-area: icon;
    background-color: ${isActive
      ? `var(${BUSINESS_SOLUTIONS_VARIABLES.primaryTextColor}) `
      : `var(${BUSINESS_SOLUTIONS_VARIABLES.tertiaryTextColor}) `};
    mask-image: url(${url});
    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: contain;
    width: 5rem;
    aspect-ratio: 1 / 1;
  `}
`;

const Label = styled('h4', filterProps('isActive'))<{ isActive: boolean }>`
  ${({ theme: { tp }, isActive }) => css`
    grid-area: label;
    margin: 0;
    width: 16.25rem;
    text-align: center;
    line-height: 1.36;
    color: ${isActive
      ? `var(${BUSINESS_SOLUTIONS_VARIABLES.primaryTextColor}) `
      : `var(${BUSINESS_SOLUTIONS_VARIABLES.tertiaryTextColor}) `};
    font-size: 1.75rem;

    ${tp.useFont(fonts => fonts['Nunito Sans'][isActive ? 'veryBold' : 'semiBold'])};
  `}
`;

const SolutionsSelect = styled(MobileSelect)`
  ${({ theme: { mq } }) => css`
    display: none;

    ${mq.md} {
      ${MOBILE_SELECT_VARIABLES.backgroundColor}: var(${BUSINESS_SOLUTIONS_VARIABLES.mobileSelectColor});
      ${MOBILE_SELECT_VARIABLES.textColor}: var(${BUSINESS_SOLUTIONS_VARIABLES.tertiaryTextColor});
      ${MOBILE_SELECT_VARIABLES.activeTextColor}: var(${BUSINESS_SOLUTIONS_VARIABLES.primaryTextColor});

      display: block;
      z-index: 3;
      margin-top: 7.75rem;
    }
  `}
`;

const SolutionsTabs: React.FC<ISolutionsTabs> = props => {
  const { solutions, activeIndex, handleChange } = props;

  const { durations } = useTheme();
  const exitedIndex = useRef<number | null>(null);

  const handleMouseEnter = useCallback(
    (index: number) => {
      if (activeIndex === null) handleChange(index);
      else {
        setTimeout(() => {
          if (index !== exitedIndex?.current) {
            handleChange(index);
          }
        }, Number(durations.regular.slice(0, -2)) * 2);
      }
    },
    [handleChange, durations, activeIndex]
  );

  return (
    <>
      <Container>
        {solutions.map(({ icon, title }, index) => (
          <Tab
            key={title}
            isActive={activeIndex === index}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={() => {
              exitedIndex.current = index;
            }}
            onClick={() => handleChange(index)}
          >
            <Icon isActive={activeIndex === index} {...icon} />
            <Label isActive={activeIndex === index}>{title}</Label>
          </Tab>
        ))}
      </Container>
      <SolutionsSelect {...useSolutionsSelectProps(props)} />
    </>
  );
};

export const contentBusinessSolutionsSolutionsTabsFragment = graphql`
  fragment HomeContentBusinessSolutionsSolutionsTabsData on PrismicHomeDataContentHomebusinesssolutionsItem {
    home_business_solutions_panel_title
    home_business_solutions_panel_icon {
      url
      alt
    }
  }
`;

export default SolutionsTabs;
